/**
 * Secure Image Type Detection Utility
 * 
 * This utility uses magic bytes (file signatures) to detect real image file types.
 * 
 * SECURITY NOTE: We do NOT trust:
 * - file.name extension (can be renamed: text.txt -> image.png)
 * - file.type MIME type (browser-provided, can be spoofed)
 * - accept="image/*" attribute (only filters by extension/MIME)
 * 
 * Instead, we read the actual file bytes and check the file signature (magic bytes)
 * at the beginning of the file. This is the only reliable way to detect the real file format.
 */

export type ImageType = 'png' | 'jpeg' | 'webp' | 'gif';

/** Image or PDF document type (validated via magic bytes) */
export type DocumentType = ImageType | 'pdf';

export interface ImageValidationResult {
  isValid: boolean;
  type: ImageType | null;
  error?: string;
}

export interface DocumentValidationResult {
  isValid: boolean;
  type: DocumentType | null;
  error?: string;
}

/**
 * Magic bytes (file signatures) for supported image and document formats
 * These are the actual bytes at the start of each file type
 */
const MAGIC_BYTES = {
  // PDF: 25 50 44 46 (%PDF)
  pdf: [0x25, 0x50, 0x44, 0x46] as const,

  // PNG: 89 50 4E 47 0D 0A 1A 0A
  png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  
  // JPEG: FF D8 FF (followed by various markers)
  jpeg: [0xff, 0xd8, 0xff],
  
  // WEBP: RIFF...WEBP
  // First 4 bytes: 52 49 46 46 (RIFF)
  // Bytes 8-11: 57 45 42 50 (WEBP)
  webp: {
    riff: [0x52, 0x49, 0x46, 0x46], // RIFF
    webp: [0x57, 0x45, 0x42, 0x50], // WEBP (at offset 8)
  },
  
  // GIF: 47 49 46 38 (GIF8) followed by 37 or 39 (GIF87a or GIF89a)
  gif: [0x47, 0x49, 0x46, 0x38], // GIF8
};

/**
 * Reads the first bytes of a file to detect its real format using magic bytes
 * 
 * @param file - The File object to validate
 * @returns Promise resolving to ImageType if valid image, null otherwise
 * 
 * @example
 * const file = event.target.files[0];
 * const imageType = await detectRealImageType(file);
 * if (imageType) {
 *   console.log(`Valid ${imageType} image`);
 * } else {
 *   console.log('Not a valid image file');
 * }
 */
export async function detectRealImageType(
  file: File
): Promise<ImageType | null> {
  return new Promise((resolve, reject) => {
    // Only read first 12 bytes (enough to detect all formats)
    // This is efficient even for large files
    const reader = new FileReader();
    const blob = file.slice(0, 12); // Read only first 12 bytes

    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (!arrayBuffer) {
          resolve(null);
          return;
        }

        const bytes = new Uint8Array(arrayBuffer);

        // Check PNG: 89 50 4E 47 0D 0A 1A 0A
        if (
          bytes.length >= 8 &&
          bytes[0] === MAGIC_BYTES.png[0] &&
          bytes[1] === MAGIC_BYTES.png[1] &&
          bytes[2] === MAGIC_BYTES.png[2] &&
          bytes[3] === MAGIC_BYTES.png[3] &&
          bytes[4] === MAGIC_BYTES.png[4] &&
          bytes[5] === MAGIC_BYTES.png[5] &&
          bytes[6] === MAGIC_BYTES.png[6] &&
          bytes[7] === MAGIC_BYTES.png[7]
        ) {
          resolve('png');
          return;
        }

        // Check JPEG: FF D8 FF
        if (
          bytes.length >= 3 &&
          bytes[0] === MAGIC_BYTES.jpeg[0] &&
          bytes[1] === MAGIC_BYTES.jpeg[1] &&
          bytes[2] === MAGIC_BYTES.jpeg[2]
        ) {
          resolve('jpeg');
          return;
        }

        // Check WEBP: RIFF (4 bytes) ... WEBP (at offset 8)
        if (
          bytes.length >= 12 &&
          bytes[0] === MAGIC_BYTES.webp.riff[0] &&
          bytes[1] === MAGIC_BYTES.webp.riff[1] &&
          bytes[2] === MAGIC_BYTES.webp.riff[2] &&
          bytes[3] === MAGIC_BYTES.webp.riff[3] &&
          bytes[8] === MAGIC_BYTES.webp.webp[0] &&
          bytes[9] === MAGIC_BYTES.webp.webp[1] &&
          bytes[10] === MAGIC_BYTES.webp.webp[2] &&
          bytes[11] === MAGIC_BYTES.webp.webp[3]
        ) {
          resolve('webp');
          return;
        }

        // Check GIF: 47 49 46 38 (GIF8) followed by 37 or 39
        if (
          bytes.length >= 6 &&
          bytes[0] === MAGIC_BYTES.gif[0] &&
          bytes[1] === MAGIC_BYTES.gif[1] &&
          bytes[2] === MAGIC_BYTES.gif[2] &&
          bytes[3] === MAGIC_BYTES.gif[3] &&
          (bytes[4] === 0x37 || bytes[4] === 0x39) && // '7' or '9'
          bytes[5] === 0x61 // 'a'
        ) {
          resolve('gif');
          return;
        }

        // Not a recognized image format
        resolve(null);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Validates if a file is a real image file using magic bytes
 * 
 * @param file - The File object to validate
 * @returns Promise resolving to validation result with type and error message
 * 
 * @example
 * const result = await validateImageFile(file);
 * if (result.isValid) {
 *   console.log(`Valid ${result.type} image`);
 * } else {
 *   console.error(result.error);
 * }
 */
export async function validateImageFile(
  file: File
): Promise<ImageValidationResult> {
  try {
    const imageType = await detectRealImageType(file);

    if (!imageType) {
      return {
        isValid: false,
        type: null,
        error: `File "${file.name}" is not a valid image file. Only PNG, JPEG, WEBP, and GIF images are allowed.`,
      };
    }

    return {
      isValid: true,
      type: imageType,
    };
  } catch (error) {
    return {
      isValid: false,
      type: null,
      error: `Error validating file "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Validates if a file is a real image or PDF using magic bytes (file signatures).
 * Checks PDF first (%PDF), then image types.
 *
 * @param file - The File object to validate
 * @returns Promise resolving to validation result with type (image or 'pdf') and error message
 */
export async function validateDocumentFile(
  file: File
): Promise<DocumentValidationResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    const blob = file.slice(0, 4);

    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (!arrayBuffer) {
          const imageResult = await validateImageFile(file);
          resolve({
            isValid: imageResult.isValid,
            type: imageResult.type,
            error: imageResult.error,
          });
          return;
        }

        const bytes = new Uint8Array(arrayBuffer);

        // Check PDF: %PDF (25 50 44 46)
        if (
          bytes.length >= 4 &&
          bytes[0] === MAGIC_BYTES.pdf[0] &&
          bytes[1] === MAGIC_BYTES.pdf[1] &&
          bytes[2] === MAGIC_BYTES.pdf[2] &&
          bytes[3] === MAGIC_BYTES.pdf[3]
        ) {
          resolve({ isValid: true, type: 'pdf' });
          return;
        }

        // Not PDF - validate as image
        const imageResult = await validateImageFile(file);
        resolve({
          isValid: imageResult.isValid,
          type: imageResult.type,
          error: imageResult.isValid
            ? undefined
            : `File "${file.name}" is not a valid document. Only PNG, JPEG, WEBP, GIF, and PDF are allowed.`,
        });
      } catch (error) {
        resolve({
          isValid: false,
          type: null,
          error: `Error validating file "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    };

    reader.onerror = () => {
      resolve({
        isValid: false,
        type: null,
        error: `Failed to read file "${file.name}"`,
      });
    };

    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Checks if a file extension matches common image extensions
 * This is only for UI display purposes, NOT for security validation
 * 
 * @param filename - The filename to check
 * @returns true if extension looks like an image (for display only)
 */
export function hasImageExtension(filename: string): boolean {
  const extension = filename.toLowerCase().split('.').pop();
  return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(extension || '');
}
